"use client";

import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { useMemo, useRef } from "react";
import * as THREE from "three";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface ShaderPlaneProps {
    vertexShader: string;
    fragmentShader: string;
    uniforms: { [key: string]: { value: unknown } };
}

const ShaderPlane = ({
    vertexShader,
    fragmentShader,
    uniforms,
}: ShaderPlaneProps) => {
    const meshRef = useRef<THREE.Mesh>(null);
    const { size } = useThree();

    useFrame((state) => {
        if (meshRef.current) {
            const material = meshRef.current.material as THREE.ShaderMaterial;
            material.uniforms.u_time.value = state.clock.elapsedTime * 0.5;
            material.uniforms.u_resolution.value.set(size.width, size.height, 1.0);
        }
    });

    return (
        <mesh ref={meshRef}>
            <planeGeometry args={[2, 2]} />
            <shaderMaterial
                vertexShader={vertexShader}
                fragmentShader={fragmentShader}
                uniforms={uniforms}
                side={THREE.FrontSide}
                depthTest={false}
                depthWrite={false}
            />
        </mesh>
    );
};

const vertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`;

const fragmentShader = `
  precision highp float;

  varying vec2 vUv;
  uniform float u_time;
  uniform vec3 u_resolution;

  vec2 toPolar(vec2 p) {
      float r = length(p);
      float a = atan(p.y, p.x);
      return vec2(r, a);
  }

  vec2 fromPolar(vec2 polar) {
      return vec2(cos(polar.y), sin(polar.y)) * polar.x;
  }

  void mainImage(out vec4 fragColor, in vec2 fragCoord) {
      vec2 p = 6.0 * ((fragCoord.xy - 0.5 * u_resolution.xy) / u_resolution.y);

      vec2 polar = toPolar(p);
      float r = polar.x;
      float a = polar.y;

      vec2 i = p;
      float c = 0.0;
      float rot = r + u_time + p.x * 0.100;
      for (float n = 0.0; n < 4.0; n++) {
          float rr = r + 0.15 * sin(u_time*0.7 + float(n) + r*2.0);
          p *= mat2(
              cos(rot - sin(u_time / 10.0)), sin(rot),
              -sin(cos(rot) - u_time / 10.0), cos(rot)
          ) * -0.25;

          float t = r - u_time / (n + 30.0);
          i -= p + sin(t - i.y) + rr;

          c += 2.2 / length(vec2(
              (sin(i.x + t) / 0.15),
              (cos(i.y + t) / 0.15)
          ));
      }

      c /= 8.0;

      vec3 baseColor = vec3(0.5, 0.92, 0.0);
      vec3 finalColor = baseColor * smoothstep(0.0, 1.0, c * 0.6);

      fragColor = vec4(finalColor, 1.0);
  }

  void main() {
      vec4 fragColor;
      vec2 fragCoord = vUv * u_resolution.xy;
      mainImage(fragColor, fragCoord);
      gl_FragColor = fragColor;
  }
`;

interface HeroProps {
    title: string;
    description: string;
    badgeText?: string;
    badgeLabel?: string;
    ctaButtons?: Array<{ text: string; href?: string; primary?: boolean }>;
    microDetails?: Array<string>;
}

const SyntheticHero = ({
    title = "Launch your dream MVP in just one week.",
    description = "We turn your idea into a working product — fast, functional, and ready for investors or early users. No waiting. No overbuilding. Just pure execution.",
    badgeText = "OneWeekMVPs",
    badgeLabel = "Launch in 7 Days",
    ctaButtons = [
        { text: "Start Your MVP", href: "https://cal.com/buildwithrishi/15min", primary: true },
        { text: "See How It Works", href: "#process" },
    ],
    microDetails = [
        "7-day build framework",
        "Investor-ready designs",
        "Real users, real traction",
    ],
}: HeroProps) => {
    const sectionRef = useRef<HTMLElement | null>(null);
    const badgeWrapperRef = useRef<HTMLDivElement | null>(null);
    const headingRef = useRef<HTMLHeadingElement | null>(null);
    const paragraphRef = useRef<HTMLParagraphElement | null>(null);
    const ctaRef = useRef<HTMLDivElement | null>(null);
    const microRef = useRef<HTMLUListElement | null>(null);
    const shaderUniforms = useMemo(
        () => ({
            u_time: { value: 0 },
            u_resolution: { value: new THREE.Vector3(1, 1, 1) },
        }),
        [],
    );

    useGSAP(
        () => {
            if (!headingRef.current) return;

            document.fonts.ready.then(() => {
                if (badgeWrapperRef.current) {
                    gsap.set(badgeWrapperRef.current, { autoAlpha: 0, y: -8 });
                }
                if (headingRef.current) {
                    gsap.set(headingRef.current, { autoAlpha: 0, y: 24, filter: "blur(16px)" });
                }
                if (paragraphRef.current) {
                    gsap.set(paragraphRef.current, { autoAlpha: 0, y: 8 });
                }
                if (ctaRef.current) {
                    gsap.set(ctaRef.current, { autoAlpha: 0, y: 8 });
                }

                const microItems = microRef.current
                    ? Array.from(microRef.current.querySelectorAll("li"))
                    : [];
                if (microItems.length > 0) {
                    gsap.set(microItems, { autoAlpha: 0, y: 6 });
                }

                const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

                if (badgeWrapperRef.current) {
                    tl.to(
                        badgeWrapperRef.current,
                        { autoAlpha: 1, y: 0, duration: 0.5 },
                        0,
                    );
                }

                if (headingRef.current) {
                    tl.to(
                        headingRef.current,
                        {
                            filter: "blur(0px)",
                            y: 0,
                            autoAlpha: 1,
                            duration: 0.9,
                        },
                        0.1,
                    );
                }

                if (paragraphRef.current) {
                    tl.to(
                        paragraphRef.current,
                        { autoAlpha: 1, y: 0, duration: 0.5 },
                        "-=0.55",
                    );
                }

                if (ctaRef.current) {
                    tl.to(
                        ctaRef.current,
                        { autoAlpha: 1, y: 0, duration: 0.5 },
                        "-=0.35",
                    );
                }

                if (microItems.length > 0) {
                    tl.to(
                        microItems,
                        { autoAlpha: 1, y: 0, duration: 0.5, stagger: 0.1 },
                        "-=0.25",
                    );
                }
            });
        },
        { scope: sectionRef },
    );

    return (
        <section
            ref={sectionRef}
            className="relative flex items-center justify-center min-h-screen overflow-hidden bg-background"
        >
            <div className="absolute inset-0 z-0 opacity-40">
                <Canvas
                    gl={{ antialias: true, alpha: true }}
                    dpr={[1, 2]}
                    style={{ width: "100%", height: "100%" }}
                >
                    <ShaderPlane
                        vertexShader={vertexShader}
                        fragmentShader={fragmentShader}
                        uniforms={shaderUniforms}
                    />
                </Canvas>
            </div>
            <div className="absolute inset-0 z-[1] bg-gradient-to-b from-background/60 via-transparent to-background/60" />
            <div className="relative z-10 flex flex-col items-center text-center px-6 py-24">
                <div ref={badgeWrapperRef}>
                    <Badge className="mb-6 bg-background/80 hover:bg-background/90 text-foreground backdrop-blur-md border border-border/50 uppercase tracking-wider font-medium flex items-center gap-2 px-4 py-1.5">
                        <span className="text-[10px] font-light tracking-[0.18em] text-muted-foreground">
                            {badgeLabel}
                        </span>
                        <span className="h-1 w-1 rounded-full bg-primary" />
                        <span className="text-xs font-light tracking-tight text-primary">
                            {badgeText}
                        </span>
                    </Badge>
                </div>

                <h1
                    ref={headingRef}
                    className="text-4xl md:text-7xl max-w-4xl font-display font-medium tracking-tight text-foreground mb-4"
                >
                    {title}
                </h1>

                <p
                    ref={paragraphRef}
                    className="text-white text-base md:text-xl max-w-2xl mx-auto leading-relaxed py-4 font-light"
                >
                    {description}
                </p>

                {/* Video Frame */}
                <div className="mt-8 mb-12 w-full px-4">
                    <div className="inline-block p-[2px] md:p-[6px] rounded-lg md:rounded-2xl backdrop-blur-xl bg-white/25 border border-white/25 w-full">
                        <iframe
                            className="w-full max-w-7xl aspect-video rounded-lg md:rounded-xl"
                            src="https://www.youtube.com/embed/jCMKhgW1mFE?si=eaIMWbdJAuWaPigT&controls=0&autoplay=1&mute=1"
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen
                        />
                    </div>
                </div>

                <div
                    ref={ctaRef}
                    className="flex flex-wrap items-center justify-center gap-4"
                >
                    {ctaButtons.map((button, index) => {
                        const isPrimary = button.primary ?? index === 0;

                        if (button.href) {
                            return (
                                <Button
                                    key={index}
                                    variant={isPrimary ? undefined : "outline"}
                                    size="lg"
                                    asChild
                                >
                                    <a href={button.href}>{button.text}</a>
                                </Button>
                            );
                        }

                        return (
                            <Button
                                key={index}
                                variant={isPrimary ? undefined : "outline"}
                                size="lg"
                            >
                                {button.text}
                            </Button>
                        );
                    })}
                </div>

                {microDetails.length > 0 && (
                    <ul
                        ref={microRef}
                        className="mt-8 flex flex-wrap justify-center gap-6 tracking-tight text-white text-sm"
                    >
                        {microDetails.map((detail, index) => (
                            <li key={index} className="flex items-center gap-2">
                                <span className="h-1 w-1 rounded-full bg-primary" />
                                {detail}
                            </li>
                        ))}
                    </ul>
                )}

            </div>
        </section>
    );
};

export default SyntheticHero;

